import React, { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { HomeIcon, ListIcon, BarChart3Icon, BellIcon, UserIcon, SearchIcon, SettingsIcon, LogOutIcon, CreditCardIcon, InfoIcon, CheckCircleIcon, AlertCircleIcon, MenuIcon, XIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const {
    user,
    logout
  } = useAuth();
  // Mock notifications data
  const notifications = [{
    id: 1,
    type: 'billing',
    title: 'Netflix subscription renewed',
    message: 'Your Netflix subscription was successfully renewed for $15.99',
    time: '2 hours ago',
    read: false,
    icon: <CreditCardIcon size={16} className="text-blue-500" data-id="element-84" />
  }, {
    id: 2,
    type: 'alert',
    title: 'Spotify price increase',
    message: 'Spotify is increasing their price by $2 starting next month',
    time: '1 day ago',
    read: false,
    icon: <AlertCircleIcon size={16} className="text-yellow-500" data-id="element-85" />
  }, {
    id: 3,
    type: 'info',
    title: 'New feature available',
    message: 'Try our new budget analytics dashboard for better insights',
    time: '3 days ago',
    read: true,
    icon: <InfoIcon size={16} className="text-purple-500" data-id="element-86" />
  }, {
    id: 4,
    type: 'success',
    title: 'Payment method updated',
    message: 'Your payment method has been successfully updated',
    time: '1 week ago',
    read: true,
    icon: <CheckCircleIcon size={16} className="text-green-500" data-id="element-87" />
  }];
  const isActive = (path: string) => {
    return location.pathname === path ? 'bg-teal-100 text-teal-700' : 'text-gray-700 hover:bg-gray-100';
  };
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) && !(event.target as HTMLElement).closest('.mobile-menu-button')) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Close mobile menu when navigating
  useEffect(() => {
    setShowMobileMenu(false);
    setShowMobileSearch(false);
  }, [location.pathname]);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'U';
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };
  // Count unread notifications
  const unreadCount = notifications.filter(notification => !notification.read).length;
  return <div className="flex flex-col h-screen bg-gray-50" data-id="element-88">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-3 px-4 sm:px-6 flex items-center justify-between" data-id="element-89">
        <div className="flex items-center" data-id="element-90">
          <button className="md:hidden p-2 mr-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors mobile-menu-button" onClick={() => setShowMobileMenu(!showMobileMenu)} aria-label="Menu" data-id="element-91">
            <MenuIcon size={20} data-id="element-92" />
          </button>
          <h1 className="text-xl font-bold text-teal-700" data-id="element-93">SubTracker</h1>
        </div>
        <div className="flex items-center space-x-3 sm:space-x-6" data-id="element-94">
          <div className="relative hidden md:block" data-id="element-95">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-96" />
            <input type="text" placeholder="Search..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 w-full sm:w-auto" data-id="element-97" />
          </div>
          {/* Mobile Search Button */}
          <button className="md:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setShowMobileSearch(!showMobileSearch)} aria-label="Search" data-id="element-98">
            <SearchIcon size={20} data-id="element-99" />
          </button>
          {/* Notification Bell */}
          <div className="relative" ref={notificationRef} data-id="element-100">
            <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" onClick={() => {
            setShowNotifications(!showNotifications);
            setShowProfileMenu(false);
          }} aria-label="Notifications" data-id="element-101">
              <BellIcon size={20} data-id="element-102" />
              {unreadCount > 0 && <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-medium rounded-full" data-id="element-103">
                  {unreadCount}
                </span>}
            </button>
            {/* Notifications Dropdown */}
            {showNotifications && <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg py-2 z-50 border border-gray-100 transform transition-all duration-200 ease-out" data-id="element-104">
                <div className="px-4 py-3 border-b border-gray-100" data-id="element-105">
                  <div className="flex items-center justify-between" data-id="element-106">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-107">
                      Notifications
                    </h3>
                    <button className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-108">
                      Mark all as read
                    </button>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto" data-id="element-109">
                  {notifications.length > 0 ? <div data-id="element-110">
                      {notifications.map(notification => <div key={notification.id} className={`px-4 py-3 hover:bg-gray-50 border-l-2 ${notification.read ? 'border-transparent' : 'border-blue-500 bg-blue-50/50'}`} data-id="element-111">
                          <div className="flex items-start" data-id="element-112">
                            <div className="flex-shrink-0 mr-3 mt-0.5" data-id="element-113">
                              {notification.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-114">
                              <p className={`text-sm font-medium ${notification.read ? 'text-gray-700' : 'text-gray-900'}`} data-id="element-115">
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2" data-id="element-116">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1" data-id="element-117">
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div> : <div className="px-4 py-6 text-center" data-id="element-118">
                      <p className="text-sm text-gray-500" data-id="element-119">
                        No notifications yet
                      </p>
                    </div>}
                </div>
                {notifications.length > 0 && <div className="px-4 py-3 border-t border-gray-100 text-center" data-id="element-120">
                    <button className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-121">
                      View all notifications
                    </button>
                  </div>}
              </div>}
          </div>
          {/* User Profile Menu */}
          <div className="relative" ref={profileMenuRef} data-id="element-122">
            <button className="flex items-center justify-center rounded-full overflow-hidden hover:ring-2 hover:ring-teal-500 transition-all" onClick={() => {
            setShowProfileMenu(!showProfileMenu);
            setShowNotifications(false);
          }} aria-label="User menu" data-id="element-123">
              {user && user.profilePicture ? <img src={user.profilePicture} alt={`${user.firstName} ${user.lastName}`} className="w-9 h-9 rounded-full object-cover" data-id="element-124" /> : <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium" data-id="element-125">
                  {getUserInitials()}
                </div>}
            </button>
            {/* Modern Profile Dropdown Menu */}
            {showProfileMenu && <div className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg py-2 z-50 border border-gray-100 transform transition-all duration-200 ease-out" data-id="element-126">
                <div className="px-4 py-3 border-b border-gray-100" data-id="element-127">
                  <div className="flex items-center space-x-3" data-id="element-128">
                    {user && user.profilePicture ? <img src={user.profilePicture} alt={`${user.firstName} ${user.lastName}`} className="w-10 h-10 rounded-full object-cover" data-id="element-129" /> : <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium" data-id="element-130">
                        {getUserInitials()}
                      </div>}
                    <div data-id="element-131">
                      <p className="text-sm font-medium text-gray-900" data-id="element-132">
                        {user ? `${user.firstName} ${user.lastName}` : 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate" data-id="element-133">
                        {user?.email || 'user@example.com'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="py-1" data-id="element-134">
                  <button onClick={() => {
                navigate('/settings');
                setShowProfileMenu(false);
              }} className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors" data-id="element-135">
                    <SettingsIcon className="mr-3 text-gray-500" size={16} data-id="element-136" />
                    Settings
                  </button>
                  <button onClick={handleLogout} className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors" data-id="element-137">
                    <LogOutIcon className="mr-3 text-gray-500" size={16} data-id="element-138" />
                    Sign out
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </header>
      {/* Mobile Search Bar */}
      {showMobileSearch && <div className="md:hidden bg-white px-4 py-2 border-b border-gray-200" data-id="element-139">
          <div className="relative" data-id="element-140">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-141" />
            <input type="text" placeholder="Search subscriptions..." className="pl-10 pr-4 py-2.5 w-full border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" autoFocus data-id="element-142" />
          </div>
        </div>}
      <div className="flex flex-1 overflow-hidden" data-id="element-143">
        {/* Mobile Sidebar */}
        {showMobileMenu && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 md:hidden" onClick={() => setShowMobileMenu(false)} data-id="element-144">
            <div className="fixed inset-y-0 left-0 flex flex-col w-64 max-w-[80%] bg-white h-full shadow-xl transform transition-all duration-300 ease-in-out" ref={mobileMenuRef} onClick={e => e.stopPropagation()} data-id="element-145">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200" data-id="element-146">
                <h2 className="text-xl font-bold text-teal-700" data-id="element-147">SubTracker</h2>
                <button onClick={() => setShowMobileMenu(false)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-148">
                  <XIcon size={20} data-id="element-149" />
                </button>
              </div>
              <nav className="flex-1 px-2 pt-5 pb-4 space-y-1 overflow-y-auto" data-id="element-150">
                <Link to="/" className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive('/')}`} onClick={() => setShowMobileMenu(false)} data-id="element-151">
                  <HomeIcon className="mr-3" size={20} data-id="element-152" />
                  Home
                </Link>
                <Link to="/subscriptions" className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive('/subscriptions')}`} onClick={() => setShowMobileMenu(false)} data-id="element-153">
                  <ListIcon className="mr-3" size={20} data-id="element-154" />
                  Subscriptions
                </Link>
                <Link to="/insights" className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive('/insights')}`} onClick={() => setShowMobileMenu(false)} data-id="element-155">
                  <BarChart3Icon className="mr-3" size={20} data-id="element-156" />
                  Insights
                </Link>
                <div className="border-t border-gray-200 my-3" data-id="element-157"></div>
                <Link to="/settings" className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive('/settings')}`} onClick={() => setShowMobileMenu(false)} data-id="element-158">
                  <SettingsIcon className="mr-3" size={20} data-id="element-159" />
                  Settings
                </Link>
              </nav>
              <div className="p-4 border-t border-gray-200" data-id="element-160">
                <button onClick={handleLogout} className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors rounded-md" data-id="element-161">
                  <LogOutIcon className="mr-3 text-gray-500" size={16} data-id="element-162" />
                  Sign out
                </button>
              </div>
            </div>
          </div>}
        {/* Desktop Sidebar */}
        <aside className="hidden md:block w-64 bg-white border-r border-gray-200 pt-5 pb-4 flex flex-col" data-id="element-163">
          <nav className="flex-1 px-2 space-y-1" data-id="element-164">
            <Link to="/" className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive('/')}`} data-id="element-165">
              <HomeIcon className="mr-3" size={20} data-id="element-166" />
              Home
            </Link>
            <Link to="/subscriptions" className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive('/subscriptions')}`} data-id="element-167">
              <ListIcon className="mr-3" size={20} data-id="element-168" />
              Subscriptions
            </Link>
            <Link to="/insights" className={`flex items-center px-4 py-3 text-sm font-medium rounded-md ${isActive('/insights')}`} data-id="element-169">
              <BarChart3Icon className="mr-3" size={20} data-id="element-170" />
              Insights
            </Link>
          </nav>
        </aside>
        {/* Main content */}
        <main className="flex-1 overflow-auto p-4 sm:p-6" data-id="element-171">
          <Outlet data-id="element-172" />
        </main>
      </div>
    </div>;
};
export default Layout;